import { useContext, useEffect, useState } from "react";
import { useAccess } from "../../../../access/hooks/useAccess";
import { billingAllows } from "../../billingAccess";
import { SectionHeader } from "../../../ui";
import { Context } from "../../../../context/Context";
import { getSettingsSnapshotAPI } from "../../../../utils/api/billing";
import SettingsSection from "./SettingsSection";
import PackagesPanel from "./PackagesPanel";
import ServicesPanel from "./ServicesPanel";
import TaxPanel from "./TaxPanel";
import CouponsPanel from "./CouponsPanel";
import { useBillingAdminText } from "../billingAdminI18n";

const SettingsTab = ({ access = null } = {}) => {
  const { lang = "en" } = useContext(Context) || {};
  const { can } = useAccess();
  const text = useBillingAdminText(lang);
  const token = localStorage.getItem("auth-token");
  const canView = billingAllows(can("billing.admin.view"), access?.canViewBillingAdmin);
  const canManage = billingAllows(can("billing.admin.settings.manage"), access?.canManageBillingSettings);
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState("");
  const [openSections, setOpenSections] = useState(new Set());

  const load = async () => {
    if (!canView || !token) return;
    setError("");
    try {
      const result = await getSettingsSnapshotAPI(token);
      setSnapshot(result.data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const toggleSection = (key) => {
    setOpenSections((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (!canView) {
    return <div className="billing-admin-denied">{text.settings.denied}</div>;
  }

  return (
    <section className="billing-settings-v141">
      <SectionHeader title={text.settings.title141} description={text.settings.description141} />
      {error && <div className="billing-error">{error}</div>}

      <div className="billing-kpi-row">
        <div className="billing-kpi-card">
          <span>{text.settings.snapshot.packagesAvailable}</span>
          <strong>{snapshot?.packagesAvailable ?? "-"}</strong>
        </div>
        <div className="billing-kpi-card">
          <span>{text.settings.snapshot.servicesActive}</span>
          <strong>
            {snapshot?.activeServicesCount ?? "-"}/{snapshot?.totalServicesCount ?? "-"}
          </strong>
        </div>
        <div className="billing-kpi-card">
          <span>{text.settings.snapshot.couponsActive}</span>
          <strong>
            {snapshot?.couponsActive ?? 0}/{snapshot?.couponsTotal ?? 0}
          </strong>
        </div>
        <div className="billing-kpi-card">
          <span>{text.settings.snapshot.vatStatus}</span>
          <strong>{snapshot?.vatRegistered ? text.settings.snapshot.vatEnabled : text.settings.snapshot.vatDisabled}</strong>
        </div>
      </div>

      <div className="billing-settings-sections">
        <SettingsSection
          title={text.settings.entryPoints.packages}
          hint={text.settings.entryPoints.packagesHint}
          isOpen={openSections.has("packages")}
          onToggle={() => toggleSection("packages")}
        >
          <PackagesPanel canManage={canManage} onChanged={load} />
        </SettingsSection>

        <SettingsSection
          title={text.settings.entryPoints.services}
          hint={text.settings.entryPoints.servicesHint}
          isOpen={openSections.has("services")}
          onToggle={() => toggleSection("services")}
        >
          <ServicesPanel canManage={canManage} onChanged={load} />
        </SettingsSection>

        <SettingsSection
          title={text.settings.entryPoints.tax}
          hint={text.settings.entryPoints.taxHint}
          isOpen={openSections.has("tax")}
          onToggle={() => toggleSection("tax")}
        >
          <TaxPanel canManage={canManage} onChanged={load} />
        </SettingsSection>

        <SettingsSection
          title={text.settings.entryPoints.coupons}
          hint={text.settings.entryPoints.couponsHint}
          isOpen={openSections.has("coupons")}
          onToggle={() => toggleSection("coupons")}
        >
          <CouponsPanel canManage={canManage} onChanged={load} />
        </SettingsSection>
      </div>
    </section>
  );
};

export default SettingsTab;
